import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BudgetProvider, useBudget } from './store/BudgetContext';
import { ToastProvider } from './components/Toast';
import { Nav, Tab } from './components/Nav';
import { Loader, ErrorState } from './components/Loader';
import { SyncIndicator } from './components/SyncIndicator';
import { Dashboard } from './screens/Dashboard';
import { Budget } from './screens/Budget';
import { AddSheet } from './components/AddSheet';
import LoginScreen from './components/LoginSheet';
import { Goals } from './screens/Goals';
import { Inbox } from './screens/Inbox';
import { History } from './screens/History';
import { Trends } from './screens/Trends';
import { Settings } from './screens/Settings';
import { Categories } from './screens/Categories';
import { Icon } from './components/Icon';
import { api } from './api/client';
import { migrateDeviceState } from './storage/migrate-device-state';
import { coinsBalance } from './utils/game';
import { theme } from './theme';
import { useDailyClaim } from './hooks/useDailyClaim';
import { useAppBadge } from './hooks/useAppBadge';
import { buildInboxEvents, readSet, unreadCount } from './utils/inbox-events';

export default function App() {
  return (
    <ToastProvider>
      <BudgetProvider>
        <Shell />
      </BudgetProvider>
    </ToastProvider>
  );
}

type Modal = null | 'settings' | 'categories';
type CategoriesInitialTab = 'list' | 'order' | 'check';

const TABS: Tab[] = ['dashboard', 'budget', 'history', 'trends', 'inbox', 'goals'];

/** Deep-link ?tab=… (нужен пушам/ярлыкам: роутера нет, вкладка — state). */
function initialTab(): Tab {
  const t = new URLSearchParams(window.location.search).get('tab');
  return TABS.includes(t as Tab) ? (t as Tab) : 'dashboard';
}

function Shell() {
  const { status, error, refresh, drainNow, needsLogin, viewData, queue, updateTx, putReserve } =
    useBudget();
  useDailyClaim();
  useAppBadge(viewData);
  const pendingInQueue = queue.filter((e) => e.status === 'pending').length;
  const [tab, setTab] = useState<Tab>(initialTab);
  const [loginOpen, setLoginOpen] = useState(false);
  const migrationRan = useRef(false);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const meRan = useRef(false);
  // forceReadKey increases when Inbox calls markAllRead, forcing useMemo re-read
  const [forceReadKey, setForceReadKey] = useState(0);
  const handleMarkedRead = useCallback(() => setForceReadKey((k) => k + 1), []);

  // после логина: закрыть форму, перезагрузить данные и слить офлайн-очередь
  const afterLogin = useCallback(() => {
    setLoginOpen(false);
    void refresh();
    void drainNow();
  }, [refresh, drainNow]);

  // одноразовый бэкфилл легаси-localStorage (до 0010) на сервер
  useEffect(() => {
    if (status !== 'ready' || !viewData || migrationRan.current) return;
    migrationRan.current = true;
    void migrateDeviceState({ data: viewData, updateTx, putReserve });
  }, [status, viewData, updateTx, putReserve]);

  // Загружаем id текущего пользователя один раз после ready (не при needsLogin)
  useEffect(() => {
    if (status !== 'ready' || needsLogin || meRan.current) return;
    meRan.current = true;
    let cancelled = false;
    api.me()
      .then((u) => { if (!cancelled) setMyUserId(u.id); })
      .catch(() => { /* тихо */ });
    return () => { cancelled = true; };
  }, [status, needsLogin]);

  const [modal, setModal] = useState<Modal>(null);
  const [categoriesInitialTab, setCategoriesInitialTab] = useState<CategoriesInitialTab>('list');
  const autoCheckRan = useRef(false);
  const [addOpen, setAddOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // автозапуск Сверки: один раз за сессию, если categories_reviewed == null и есть расходные
  useEffect(() => {
    if (
      status !== 'ready' ||
      !viewData ||
      modal !== null ||
      autoCheckRan.current ||
      viewData.settings.categories_reviewed != null
    ) return;
    const hasExpense = viewData.categories.some((c) => c.type === 'Расход');
    if (!hasExpense) return;
    autoCheckRan.current = true;
    setCategoriesInitialTab('check');
    setModal('categories');
  }, [status, viewData, modal]);

  // офлайн-честный бейдж: счётчик pending приезжает в bootstrap и живёт в снапшоте
  const pendingFromBootstrap = viewData?.pending_count;
  useEffect(() => {
    if (typeof pendingFromBootstrap === 'number') setPendingCount(pendingFromBootstrap);
  }, [pendingFromBootstrap]);

  const reloadInbox = useCallback(async () => {
    try {
      const r = await api.listPending();
      setPendingCount(r.items.length);
    } catch {
      // тихо игнорим — не блокирует
    }
  }, []);

  // Подгружаем счётчик после bootstrap'а и при возврате на вкладку
  useEffect(() => {
    if (status === 'ready') void reloadInbox();
  }, [status, reloadInbox]);

  useEffect(() => {
    if (tab === 'inbox' || tab === 'dashboard') void reloadInbox();
  }, [tab, reloadInbox]);

  // Суммарный счётчик = pending + непрочитанные события
  // forceReadKey в deps → пересчёт сразу после markAllRead
  const inboxCount = useMemo(() => {
    const events = viewData ? buildInboxEvents(viewData, myUserId) : [];
    const evUnread = unreadCount(events, readSet());
    return pendingCount + evUnread;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewData, myUserId, pendingCount, forceReadKey]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.colors.bg,
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",
        color: theme.colors.ink,
        fontFamily: theme.font,
      }}
    >
      <style>{`
        *, *::before, *::after { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        body {
          margin: 0;
          font-size: 16.5px;
          line-height: 1.5;
          font-optical-sizing: auto;
          font-variant-ligatures: common-ligatures;
          font-variant-numeric: lining-nums;
          text-rendering: optimizeLegibility;
          -webkit-font-smoothing: antialiased;
        }
        /* Заголовки-слова — Fraunces с эксцентричным WONK и высоким opsz */
        h1, h2 {
          font-family: ${theme.fontDisplay};
          font-weight: 420;
          font-variation-settings: 'opsz' 144, 'SOFT' 0, 'WONK' 1;
          letter-spacing: -0.01em;
          line-height: 1.04;
          text-wrap: balance;
        }
        /* Крупные суммы — Fraunces, но цифры выровненные и без WONK для читаемости */
        .display {
          font-family: ${theme.fontDisplay};
          font-weight: 380;
          font-variation-settings: 'opsz' 144, 'SOFT' 0, 'WONK' 0;
          font-variant-numeric: lining-nums tabular-nums;
          letter-spacing: -0.015em;
        }
        /* Каталожные подписи: широкий трекинг, малые капители-ощущение */
        .caption {
          text-transform: uppercase;
          letter-spacing: 0.18em;
          font-size: 12px;
          color: ${theme.colors.sub};
        }
        @keyframes rise { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: none } }
        .rise { animation: rise .6s cubic-bezier(.2,.6,.2,1) both }
        .bar  { transition: width .85s cubic-bezier(.2,.8,.2,1) }
        button { font-family: inherit; cursor: pointer; transition: transform .08s ease, opacity .12s ease; }
        button:active { transform: scale(.96); opacity: .82; }
        @media (prefers-reduced-motion: reduce) { .rise { animation: none } .bar { transition: none } button:active { transform: none } }
      `}</style>

      {/* top bar with sync indicator */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          background: theme.colors.bg,
          borderBottom: `1px solid ${theme.colors.line}`,
        }}
      >
        <div
          style={{
            maxWidth: theme.maxWidth,
            margin: '0 auto',
            padding: '6px 12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={() => setModal('settings')}
            aria-label="Настройки"
            style={{
              background: 'transparent',
              border: 'none',
              padding: '6px 10px',
              cursor: 'pointer',
              color: theme.colors.sub,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Icon name="gear" size={19} />
          </button>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {viewData && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  color: theme.colors.sub,
                  fontSize: 13,
                  fontVariantNumeric: 'tabular-nums',
                  padding: '6px 4px',
                }}
              >
                <Icon name="coin" size={14} /> {coinsBalance(viewData)}
              </span>
            )}
            <button
              onClick={() => setTab('inbox')}
              aria-label="Входящие"
              style={{
                background: 'transparent',
                border: 'none',
                padding: '6px 10px',
                cursor: 'pointer',
                color: tab === 'inbox' ? theme.colors.ink : theme.colors.sub,
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <Icon name="inbox" size={19} />
              {inboxCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 2,
                    minWidth: 15,
                    height: 15,
                    borderRadius: 0,
                    background: theme.colors.ink,
                    color: theme.colors.paper,
                    fontSize: 10,
                    lineHeight: '15px',
                    textAlign: 'center',
                    padding: '0 3px',
                  }}
                >
                  {inboxCount}
                </span>
              )}
            </button>
            <SyncIndicator />
          </span>
        </div>
      </div>

      {needsLogin && status === 'ready' && (
        <div style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '10px 12px 0' }}>
          {/* hairline-плашка (Антверпен 2.0 §7): без заливки и тени */}
          <div
            style={{
              border: `1px solid ${theme.colors.ink}`,
              borderRadius: theme.radius.sm,
              padding: '9px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span style={{ minWidth: 0 }}>
              <span style={{ fontSize: 13.5 }}>Сессия истекла</span>
              <span style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic' }}>
                {' '}
                · записи копятся офлайн
                {pendingInQueue > 0 && <> · {pendingInQueue} в очереди</>}
              </span>
            </span>
            <button
              onClick={() => setLoginOpen(true)}
              style={{
                background: theme.colors.ink,
                color: theme.colors.paper,
                border: 'none',
                borderRadius: theme.radius.sm,
                padding: '7px 14px',
                fontFamily: theme.fontDisplay,
                fontSize: 13,
                whiteSpace: 'nowrap',
              }}
            >
              Войти
            </button>
          </div>
        </div>
      )}

      <main style={{ maxWidth: theme.maxWidth, margin: '0 auto', paddingBottom: 80 }}>
        {status === 'loading' && <Loader />}
        {status === 'error' && needsLogin && (
          // холодный старт без сессии и без снапшота: сразу форма входа
          <LoginScreen onSuccess={afterLogin} />
        )}
        {status === 'error' && !needsLogin && (
          <ErrorState message={error ?? 'Не удалось загрузить'} onRetry={() => void refresh()} />
        )}
        {status === 'ready' && modal === null && (
          <>
            {tab === 'dashboard' && <Dashboard />}
            {tab === 'budget'    && <Budget />}
            {tab === 'history'   && <History />}
            {tab === 'trends'    && <Trends />}
            {tab === 'inbox'     && (
              <Inbox
                myUserId={myUserId}
                onNavigate={(t) => setTab(t)}
                onMarkedRead={handleMarkedRead}
              />
            )}
            {tab === 'goals'     && <Goals />}
          </>
        )}
        {status === 'ready' && modal === 'settings' && (
          <Settings
            onClose={() => setModal(null)}
            onOpenCategories={() => { setCategoriesInitialTab('list'); setModal('categories'); }}
          />
        )}
        {status === 'ready' && modal === 'categories' && (
          <Categories
            onBack={() => setModal('settings')}
            initialTab={categoriesInitialTab}
          />
        )}
      </main>

      {modal === null && (
        <Nav
          active={tab}
          onChange={(t) => (t === 'add' ? setAddOpen(true) : setTab(t))}
          inboxCount={inboxCount}
        />
      )}

      {status === 'ready' && addOpen && <AddSheet onClose={() => setAddOpen(false)} />}

      {/* re-login поверх контента (данные из снапшота остаются под формой) */}
      {loginOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 90,
            background: theme.colors.bg,
            overflowY: 'auto',
          }}
        >
          <LoginScreen onSuccess={afterLogin} />
        </div>
      )}
    </div>
  );
}
